import type { SuggestionItem } from "@digdir/designsystemet-react";
import {
    areaQueryParamKey,
    defaultSortOption,
    getDefaultSortDirectionForOption,
    ownerQueryParamKey,
    type SortDirection,
    type SortOption,
    searchQueryParamKey,
    sortDirectionQueryParamKey,
    sortOptionValues,
    sortQueryParamKey,
    targetGroupQueryParamKey,
} from "./filterDefinitions";

export function getInitialSuggestionValuesFromUrl(queryKey: string): SuggestionItem[] {
    if (typeof window === "undefined") {
        return [];
    }

    const params = new URLSearchParams(window.location.search);
    return params.getAll(queryKey).map((value) => ({ label: value, value }));
}

export function getInitialSearchTermFromUrl(): string {
    if (typeof window === "undefined") {
        return "";
    }

    const params = new URLSearchParams(window.location.search);
    return params.get(searchQueryParamKey) ?? "";
}

export function getInitialSortOptionFromUrl(): SortOption {
    if (typeof window === "undefined") {
        return defaultSortOption;
    }

    const params = new URLSearchParams(window.location.search);
    const urlSortOption = params.get(sortQueryParamKey);

    if (urlSortOption && sortOptionValues.has(urlSortOption as SortOption)) {
        return urlSortOption as SortOption;
    }

    return defaultSortOption;
}

export function getInitialSortDirectionFromUrl(sortOption: SortOption): SortDirection {
    if (typeof window === "undefined") {
        return getDefaultSortDirectionForOption(sortOption);
    }

    const params = new URLSearchParams(window.location.search);
    const urlSortDirection = params.get(sortDirectionQueryParamKey);

    if (urlSortDirection === "asc" || urlSortDirection === "desc") {
        return urlSortDirection;
    }

    return getDefaultSortDirectionForOption(sortOption);
}

export function writeStateToUrl({
    selectedAreas,
    selectedOwners,
    selectedTargetGroups,
    searchTerm,
    sortOption,
    sortDirection,
}: {
    selectedAreas: SuggestionItem[];
    selectedOwners: SuggestionItem[];
    selectedTargetGroups: SuggestionItem[];
    searchTerm: string;
    sortOption: SortOption;
    sortDirection: SortDirection;
}) {
    if (typeof window === "undefined") {
        return;
    }

    const params = new URLSearchParams(window.location.search);

    params.delete(searchQueryParamKey);
    if (searchTerm.trim().length > 0) {
        params.set(searchQueryParamKey, searchTerm);
    }

    params.delete(areaQueryParamKey);
    selectedAreas.forEach((item) => {
        params.append(areaQueryParamKey, item.value);
    });

    params.delete(ownerQueryParamKey);
    selectedOwners.forEach((item) => {
        params.append(ownerQueryParamKey, item.value);
    });

    params.delete(targetGroupQueryParamKey);
    selectedTargetGroups.forEach((item) => {
        params.append(targetGroupQueryParamKey, item.value);
    });

    params.delete(sortQueryParamKey);
    params.delete(sortDirectionQueryParamKey);

    if (sortOption !== defaultSortOption) {
        params.set(sortQueryParamKey, sortOption);
    }

    if (sortDirection !== getDefaultSortDirectionForOption(sortOption)) {
        params.set(sortDirectionQueryParamKey, sortDirection);
    }

    const query = params.toString();
    const nextUrl =
        query.length > 0
            ? `${window.location.pathname}?${query}${window.location.hash}`
            : `${window.location.pathname}${window.location.hash}`;

    window.history.replaceState(null, "", nextUrl);
}
